import classNames from "classnames";
import React, { Children, createRef, FC, ReactElement, useCallback, useEffect, useRef, useState } from "react";
import { IconButton } from "../button";
import { ContextMenu, MenuItemRenderer } from "../context-menu";
import { IconNavArrowDown, IconNavArrowUp } from "../icons";
import { Tab } from "./tab";
import { CSS_CLASSES } from "./tab-css-classes";
import { TabList } from "./tab-list";
import { TabPanel } from "./tab-panel";
import { ContextMenuTab, TabProps, UncontrolledTabsProps } from "./tab.types";
import { UncontrolledTab } from "./uncontrolled-tab";

const SPACE = 32;
const ENTER = 13;
const END = 35;
const HOME = 36;
const LEFT_ARROW = 37;
const UP_ARROW = 38;
const RIGHT_ARROW = 39;
const DOWN_ARROW = 40;

const isNode = (node: HTMLElement) => node && "getAttribute" in node;
const isTabNode = (node: HTMLElement) => isNode(node) && node.getAttribute("role") === "tab";

const isTabDisabled = (node: HTMLElement) => isNode(node) && node.getAttribute("aria-disabled") === "true";

export const UncontrolledTabs: FC<UncontrolledTabsProps> = ({
  children,
  className: classNameProps,
  ripple,
  onActivate,
  variant,
  color,
  activeIndex,
  maxTabsOnMobile,
  ...restProps
}) => {
  const containerDivRef = useRef<any>(null);
  const [canUseActiveElement, setCanUseActiveElement] = useState(false);
  const tabs = useRef<{ [key: string]: any }>({});

  useEffect(() => {
    setCanUseActiveElement(() => !!(window.document && window.document.activeElement));
  }, []);

  const isTabFromContainer = useCallback(
    (node: HTMLElement) => {
      if (!isTabNode(node)) {
        return false;
      }

      let nodeAncestor = node.parentElement;
      do {
        if (nodeAncestor === containerDivRef.current) {
          return true;
        }
        if (nodeAncestor!.getAttribute("data-tabs")) {
          break;
        }

        nodeAncestor = nodeAncestor!.parentElement;
      } while (nodeAncestor);

      return false;
    },
    [containerDivRef]
  );

  const getIndexOfNode = useCallback(
    (node: HTMLElement) => {
      if (isTabDisabled(node)) {
        return activeIndex;
      }

      const tabs: HTMLElement[] = [].slice.call(node.parentNode!.children).filter(isTabNode);

      return tabs.indexOf(node);
    },
    [activeIndex]
  );

  const getTab = (index: number) => tabs.current[`tab-id-${index}`].current;
  const getTabsCount = () => Object.keys(tabs.current).length;

  const getPrevTab = useCallback((index: number) => {
    let i = index;

    while (i--) {
      if (!isTabDisabled(getTab(i))) {
        return i;
      }
    }

    i = getTabsCount();
    while (i-- > index) {
      if (!isTabDisabled(getTab(i))) {
        return i;
      }
    }

    return index;
  }, []);

  const getNextTab = useCallback((index: number) => {
    const count = getTabsCount();

    for (let i = index + 1; i < count; i++) {
      if (!isTabDisabled(getTab(i))) {
        return i;
      }
    }

    for (let i = 0; i < index; i++) {
      if (!isTabDisabled(getTab(i))) {
        return i;
      }
    }

    return index;
  }, []);

  const getLastTab = useCallback(() => {
    let i = getTabsCount();

    while (i--) {
      if (!isTabDisabled(getTab(i))) {
        return i;
      }
    }

    return activeIndex;
  }, [activeIndex]);

  const getFirstTab = useCallback(() => {
    const count = getTabsCount();

    for (let i = 0; i < count; i++) {
      if (!isTabDisabled(getTab(i))) {
        return i;
      }
    }

    return activeIndex;
  }, [activeIndex]);

  const handleKeyDown = useCallback(
    (e: any) => {
      if (isTabFromContainer(e.target)) {
        const charCode = typeof e.which == "number" ? e.which : e.keyCode;
        let preventDefault = false;
        let useSelectedIndex = false;
        let index = activeIndex;
        if (charCode === SPACE || charCode === ENTER) {
          preventDefault = true;
          useSelectedIndex = false;
          index = getIndexOfNode(e.target);
          onActivate(e, index, activeIndex);
        }

        if (charCode === LEFT_ARROW || charCode === UP_ARROW) {
          index = getPrevTab(activeIndex);
          preventDefault = true;
          useSelectedIndex = true;
        } else if (charCode === RIGHT_ARROW || charCode === DOWN_ARROW) {
          index = getNextTab(activeIndex);
          preventDefault = true;
          useSelectedIndex = true;
        } else if (charCode === END) {
          index = getLastTab();
          preventDefault = true;
          useSelectedIndex = true;
        } else if (charCode === HOME) {
          index = getFirstTab();
          preventDefault = true;
          useSelectedIndex = true;
        }

        if (preventDefault) {
          e.preventDefault();
        }

        if (useSelectedIndex) {
          onActivate(e, index, activeIndex);
        }
      }
    },
    [activeIndex, getFirstTab, getIndexOfNode, getLastTab, getNextTab, getPrevTab, isTabFromContainer, onActivate]
  );

  const getChildren = (children: ReactElement<TabProps>[]) => {
    const tabsJsx: JSX.Element[] = [];
    const panelsJsx: JSX.Element[] = [];
    const menuItems: ContextMenuTab[] = [];
    const customMenuItemRenderer: MenuItemRenderer<ContextMenuTab> = ({ item }) => (
      <UncontrolledTab
        label={item.label}
        labelAlign={item.labelAlign}
        disabled={item.disabled}
        className={item.className}
        badge={item.badge}
        id={item.id}
        key={`custom-menu-${item.id}`}
        active={item.tabIteratingIndex === item.activeIndex}
        onTabClick={item.onTabClick}
        panelId={item.panelId}
        buttonRef={tabs.current[item.id]}
        {...item.restTabProps}
      ></UncontrolledTab>
    );

    let wasTabFocused = false;

    if (canUseActiveElement) {
      wasTabFocused = Object.values(tabs.current).some((tab: any) => document.activeElement === tab.current);
    }

    Children.forEach(children, (child, index) => {
      if (child.type !== Tab) {
        return;
      }

      const tabId = `tab-id-${index}`;
      const panelId = `panel-id-${index}`;
      tabs.current[tabId] = createRef<HTMLButtonElement>();
      const { badge, children, className, disabled, label, ...restTabProps } = child.props;
      let tabClass = className;

      if (maxTabsOnMobile <= index) {
        tabClass = classNames(className, CSS_CLASSES.TAB_DESKTOP);
        menuItems.push({
          label: label,
          labelAlign: "left",
          disabled: disabled,
          className: className,
          badge: badge,
          id: tabId,
          active: index === activeIndex,
          onTabClick: (e) => onActivate(e, index, activeIndex),
          onClick: () => {},
          panelId: panelId,
          tabIteratingIndex: index,
          activeIndex: activeIndex,
          buttonRef: tabs.current[tabId],
          restTabProps,
        });
      }

      tabsJsx.push(
        <UncontrolledTab
          label={label}
          color={color}
          labelAlign={variant === "vertical" ? "left" : "center"}
          disabled={disabled}
          className={tabClass}
          ripple={ripple}
          badge={badge}
          id={tabId}
          key={tabId}
          active={index === activeIndex}
          focus={index === activeIndex && wasTabFocused}
          onTabClick={(e) => onActivate(e, index, activeIndex)}
          panelId={panelId}
          buttonRef={tabs.current[tabId]}
          {...restTabProps}
        ></UncontrolledTab>
      );

      panelsJsx.push(
        <TabPanel id={panelId} active={index === activeIndex} tabId={tabId} key={panelId}>
          {children}
        </TabPanel>
      );
    });

    return (
      <>
        <TabList>
          {tabsJsx}
          {menuItems.length > 0 && (
            <ContextMenu
              placement="bottom-start"
              menuItems={menuItems}
              className="inline"
              menuItemRenderer={customMenuItemRenderer}
            >
              {({ open, toggleButtonProps }) => {
                return (
                  <IconButton
                    variant="ghost"
                    size="medium"
                    icon={open ? <IconNavArrowUp /> : <IconNavArrowDown />}
                    color={color}
                    label="More Tabs"
                    title="More Tabs"
                    className={CSS_CLASSES.TAB_MOBILE}
                    {...toggleButtonProps}
                  />
                );
              }}
            </ContextMenu>
          )}
        </TabList>
        {panelsJsx}
      </>
    );
  };

  const className = classNames(
    CSS_CLASSES.TABS_ROOT,
    { [CSS_CLASSES.TABS_VERTICAL]: variant === "vertical" },
    classNameProps
  );
  return (
    <div className={className} onKeyDown={handleKeyDown} ref={containerDivRef} data-tabs {...restProps}>
      {getChildren(children as ReactElement<TabProps>[])}
    </div>
  );
};
